import { useState } from "react";
import { Link, useHistory } from "react-router-dom";
import styled from "styled-components";

import { SiteNav, SiteFooter } from "../../components/fragments/ProductChrome";
import { Container } from "../../components/styles/Layout";
import useSeo from "../../hooks/useSeo";
import useJsonLd from "../../hooks/useJsonLd";
import { PRODUCT_SEO } from "../../data/seo";
import { productHomeSchema } from "../../data/structuredData";

/*
 * Doorstep Data's homepage.
 *
 * WHY IT IS NOT JUST THE TOOL. The root used to redirect to /property-report,
 * which worked but left the brand's own address with nothing on it - and the
 * tool itself is close to unindexable, because a report's results never reach
 * the URL. This page is the one place that can say what the product is in
 * words a search engine can read, and it carries the search box so saying so
 * costs nobody a click.
 *
 * The search hands off to /property-report?postcode=..., which that page now
 * reads on mount. Two inputs for one job would be the alternative - this page
 * running its own copy of the report flow - and that is how two search boxes
 * start behaving differently.
 *
 * EVERY CLAIM HERE IS CHECKABLE. The source list is the one the report
 * actually calls, the panel list is DATA_OPTIONS, and the limits section says
 * what the report says on its own cards. A homepage that promises more than
 * the product delivers is the fastest way to make the rest of it untrusted.
 */

const Hero = styled.section`
    background: #0f4c5c;
    color: #fff;
    padding: 4rem 0 3.5rem;
`;
const HeroInner = styled.div`
    max-width: 44rem;
`;
const H1 = styled.h1`
    margin: 0 0 0.9rem;
    font-size: 2.1rem;
    line-height: 1.2;
    font-weight: 700;
    letter-spacing: -0.01em;
    @media (max-width: 560px) {
        font-size: 1.7rem;
    }
`;
const Lede = styled.p`
    margin: 0 0 1.75rem;
    font-size: 1.05rem;
    line-height: 1.6;
    opacity: 0.92;
`;

/*
 * One row where it fits, a deliberate stack where it does not. The input
 * asks for 14rem and the button measures 172px, so below roughly 470px the
 * two cannot share a line - and flex-wrap on its own put the button on a
 * second row at its own width, a short orange block floating under a
 * full-width field. Stacking explicitly and letting the button fill the row
 * keeps the two halves of one control looking like one control.
 */
const SearchForm = styled.form`
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
    @media (max-width: 480px) {
        flex-direction: column;
        align-items: stretch;
    }
`;
const Input = styled.input`
    flex: 1 1 14rem;
    min-width: 0;
    padding: 0.85rem 1rem;
    font: inherit;
    font-size: 1rem;
    border: 0;
    border-radius: 6px;
    background: #fff;
    color: #121212;
`;
const Go = styled.button`
    padding: 0.85rem 1.6rem;
    font: inherit;
    font-size: 1rem;
    font-weight: 700;
    border: 0;
    border-radius: 6px;
    cursor: pointer;
    background: #e08d2f;
    color: #2b1a00;
    &:hover {
        background: #c87c26;
    }
`;
const Free = styled.p`
    margin: 0.9rem 0 0;
    font-size: 0.85rem;
    opacity: 0.8;
`;

const Section = styled.section`
    padding: 2.75rem 0 0;
`;
const H2 = styled.h2`
    margin: 0 0 0.4rem;
    font-size: 1.3rem;
    color: #0f4c5c;
`;
const Sub = styled.p`
    margin: 0 0 1.4rem;
    color: #5a6b72;
    max-width: 46rem;
    line-height: 1.6;
`;

const Grid = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(260px, 100%), 1fr));
    gap: 1rem;
`;
const Card = styled.li`
    border: 1px solid #e6e9ee;
    border-radius: 6px;
    background: #fff;
    padding: 1rem 1.1rem;
    h3 {
        margin: 0 0 0.3rem;
        font-size: 0.98rem;
        color: #0f4c5c;
    }
    p {
        margin: 0;
        font-size: 0.88rem;
        line-height: 1.55;
        color: #4a5a60;
    }
`;

const Plain = styled.ul`
    margin: 0;
    padding-left: 1.1rem;
    max-width: 46rem;
    li {
        margin-bottom: 0.45rem;
        line-height: 1.6;
        color: #4a5a60;
    }
`;

const Closing = styled.p`
    margin: 2.5rem 0 3rem;
    font-size: 0.95rem;
    a {
        font-weight: 600;
    }
`;

/* What a report holds, in the order the page shows it. Kept as plain copy
   rather than generated from DATA_OPTIONS: that list is keyed for the API and
   its labels are controls, not sentences. */
const PANELS = [
    ["Council tax", "All eight bands with the annual and monthly charge for the billing authority, and the band for the address where the register has one."],
    ["Monthly running costs", "Council tax, gas and electricity, water and broadband added up. The first two are about the property; the last two are averages, and the panel says which is which."],
    ["Energy performance", "The certificate itself - current and potential rating, floor area, running costs - plus what the 2030 rules mean for letting it."],
    ["Sold prices and comparables", "Every sale at the address since 1995, and comparable local sales restated in today's money using the Land Registry house price index."],
    ["Flood risk", "River and sea flood zones, and surface water separately - the commoner cause in London, and the one most reports leave out."],
    ["Schools", "Primary and secondary within 0.7 miles, with Ofsted outcomes under whichever framework applies."],
    ["Broadband and mobile", "Gigabit, ultrafast and superfast availability for the postcode, and mobile coverage for the local authority."],
    ["The neighbourhood", "Supermarkets, parks, stations, buses and recorded crime, each with its own radius and its own source."],
];

const SOURCES = [
    "HM Land Registry Price Paid and the UK House Price Index",
    "The EPC register, via GOV.UK",
    "MHCLG and the Welsh Government for council tax",
    "The Environment Agency for flood zones and surface water",
    "Ofcom Connected Nations for broadband and mobile",
    "The Department for Education and Ofsted for schools",
    "data.police.uk, the Planning Data Platform, the Food Standards Agency and Transport for London",
];

export default function DoorstepHome() {
    const [postcode, setPostcode] = useState("");
    const history = useHistory();

    useSeo(PRODUCT_SEO["/"]);
    useJsonLd(productHomeSchema());

    function submit(e) {
        e.preventDefault();
        const pc = postcode.trim();
        if (!pc) return;
        // The tool reads ?postcode= on mount and runs itself, so this is a
        // hand-off rather than a second implementation of the lookup.
        history.push("/property-report?postcode=" + encodeURIComponent(pc));
    }

    return (
        <>
            <SiteNav />
            <Hero>
                <Container>
                    <HeroInner>
                        <H1>Everything on the public record for one address</H1>
                        <Lede>
                            Council tax, energy performance, sold prices, running costs, flood risk,
                            schools and more &mdash; gathered from government sources, with every
                            figure dated and attributed.
                        </Lede>
                        <SearchForm onSubmit={submit}>
                            <label htmlFor="home-postcode" style={{ display: "none" }}>
                                Postcode
                            </label>
                            <Input
                                id="home-postcode"
                                type="text"
                                value={postcode}
                                onChange={(e) => setPostcode(e.target.value)}
                                placeholder="Enter a postcode"
                                autoComplete="postal-code"
                            />
                            <Go type="submit">See the report</Go>
                        </SearchForm>
                        <Free>Free, and no account needed. England and Wales.</Free>
                    </HeroInner>
                </Container>
            </Hero>

            <Container>
                <Section>
                    <H2>What a report holds</H2>
                    <Sub>
                        Thirteen sections, each one optional. Tick only what you want and the rest is
                        never fetched.
                    </Sub>
                    <Grid>
                        {PANELS.map(([title, body]) => (
                            <Card key={title}>
                                <h3>{title}</h3>
                                <p>{body}</p>
                            </Card>
                        ))}
                    </Grid>
                </Section>

                <Section>
                    <H2>Where it comes from</H2>
                    <Sub>
                        Public registers, named on every card with the date of the release they came
                        from. Nothing here is modelled or estimated unless the page says so.
                    </Sub>
                    <Plain>
                        {SOURCES.map((s) => (
                            <li key={s}>{s}</li>
                        ))}
                    </Plain>
                </Section>

                <Section>
                    <H2>What it is not</H2>
                    <Sub>
                        The limits are on the report itself, card by card. They are worth knowing
                        before you rely on any of it.
                    </Sub>
                    <Plain>
                        <li>
                            It is not a valuation. Comparable sales are the evidence an appraisal
                            would be built from, with the arithmetic shown, and never a figure for
                            your home.
                        </li>
                        <li>
                            It is not a survey or a solicitor&rsquo;s search. Local authorities
                            publish to the planning register at their own pace, and nothing recorded
                            is not the same as nothing there.
                        </li>
                        <li>
                            Some figures are averages rather than facts about the property &mdash;
                            water and broadband costs, and mobile coverage. Each says so where it
                            appears.
                        </li>
                        <li>
                            It covers England and Wales. Scotland and Northern Ireland keep separate
                            registers that are not loaded here.
                        </li>
                    </Plain>
                </Section>

                <Closing>
                    Looking at an area rather than one home?{" "}
                    <Link to="/house-prices">House prices by postcode district</Link> covers 81
                    districts across south west London and Surrey, with medians, ten-year trends and
                    recent sales.
                </Closing>
            </Container>
            <SiteFooter />
        </>
    );
}
